import { v4 as uuidv4 } from 'uuid'
import { IFileSaver } from '@/contracts/core/file/IFileSaver'
import { IFileSink } from '@/contracts/core/file/IFileSink'
import { EnumFileSaveError } from '@/contracts/core/file/enum/EnumFileSaveError'
import {
  DOWNLOAD_PATH,
  EnumDownloadMessage,
  IDownloadCredit,
  IDownloadPort,
  IDownloadStart,
} from '@/contracts/core/file/download/IDownloadMessage'

/** File System Access API: в lib.dom TypeScript 5.4 его нет, нужное описано здесь */
interface IWritableFileStream {
  write(chunk: { type: 'write', position: number, data: Uint8Array }): Promise<void>
  close(): Promise<void>
  abort(): Promise<void>
}

interface IFileHandle {
  createWritable(): Promise<IWritableFileStream>
}

export type SaveFilePicker = (options: { suggestedName: string }) => Promise<IFileHandle>

/** Поставщик порта к service worker: контроллер страницы и пара портов MessageChannel */
export interface IWorkerChannel {
  controller(): { postMessage(message: unknown, transfer: unknown[]): void } | null
  createPorts(): { page: IDownloadPort, worker: IDownloadPort }
  /** Открыть адрес потока в скрытом фрейме: браузер начинает скачивание */
  navigate(url: string): void
}

/** Сколько кусков вне очереди страница держит в памяти, пока ждёт недостающий */
export const PENDING_CHUNKS_LIMIT = 4

/** Запись по позициям в файл, выбранный пользователем в окне «Сохранить как» */
class PickerSink implements IFileSink {
  constructor(private stream: IWritableFileStream) {}

  write(offset: number, bytes: Uint8Array): Promise<void> {
    return this.stream.write({ type: 'write', position: offset, data: bytes })
  }

  close(): Promise<void> {
    return this.stream.close()
  }

  abort(): Promise<void> {
    return this.stream.abort()
  }
}

/**
 * Поток через service worker пишется только по порядку: куски вне очереди ждут недостающего
 * (не больше PENDING_CHUNKS_LIMIT), кусок уходит воркеру, когда у страницы есть его кредит.
 */
export class WorkerSink implements IFileSink {
  private expected = 0
  private pending = new Map<number, Uint8Array>()
  private credit = 0
  private closed = false
  private waiters: (() => void)[] = []

  constructor(private port: IDownloadPort, private size: number) {
    port.onmessage = (event) => {
      const data = event.data as IDownloadCredit
      if (data.type === EnumDownloadMessage.CREDIT) {
        this.credit += data.credit
        this.wake()
      }
    }
  }

  async write(offset: number, bytes: Uint8Array): Promise<void> {
    while (this.pending.size >= PENDING_CHUNKS_LIMIT && offset !== this.expected && !this.closed) {
      await this.wait()
    }
    this.throwIfClosed()
    this.pending.set(offset, bytes)
    await this.flush()
  }

  async close(): Promise<void> {
    while (this.expected < this.size && !this.closed) {
      await this.flush()
      if (this.expected < this.size) {
        await this.wait()
      }
    }
    this.throwIfClosed()
    this.closed = true
    this.port.postMessage({ type: EnumDownloadMessage.END })
    this.port.close()
  }

  async abort(): Promise<void> {
    if (this.closed) {
      return
    }
    this.closed = true
    this.port.postMessage({ type: EnumDownloadMessage.ABORT })
    this.port.close()
    this.wake()
  }

  /** Отправить воркеру все куски по порядку, на которые есть кредит */
  private async flush(): Promise<void> {
    while (!this.closed) {
      const bytes = this.pending.get(this.expected)
      if (bytes === undefined) {
        return
      }
      if (this.credit <= 0) {
        await this.wait()
        continue
      }
      this.pending.delete(this.expected)
      this.expected += bytes.length
      this.credit--
      this.port.postMessage({ type: EnumDownloadMessage.CHUNK, bytes }, [bytes.buffer as ArrayBuffer])
      this.wake()
    }
  }

  private wait(): Promise<void> {
    return new Promise((resolve) => this.waiters.push(resolve))
  }

  private wake(): void {
    const waiters = this.waiters
    this.waiters = []
    waiters.forEach((resolve) => resolve())
  }

  private throwIfClosed(): void {
    if (this.closed) {
      throw new Error(EnumFileSaveError.STREAM_CLOSED)
    }
  }
}

function browserChannel(): IWorkerChannel {
  return {
    controller: () => navigator.serviceWorker?.controller ?? null,
    createPorts: () => {
      const channel = new MessageChannel()
      // MessagePort описывает onmessage через MessageEvent; порту обмена нужно только поле data
      return { page: channel.port1 as unknown as IDownloadPort, worker: channel.port2 as unknown as IDownloadPort }
    },
    navigate: (url: string) => {
      const frame = document.createElement('iframe')
      frame.hidden = true
      frame.src = url
      document.body.appendChild(frame)
    },
  }
}

export class FileSaverV1 implements IFileSaver {
  private static instance: IFileSaver

  constructor(
    private picker: SaveFilePicker | null = typeof window === 'undefined' ? null : ((window as unknown as { showSaveFilePicker?: SaveFilePicker }).showSaveFilePicker ?? null),
    private channel: IWorkerChannel = browserChannel(),
  ) {}

  static getInstance(): IFileSaver {
    if (!FileSaverV1.instance) {
      FileSaverV1.instance = new FileSaverV1()
    }
    return FileSaverV1.instance
  }

  isSupported(): boolean {
    return this.picker !== null || this.channel.controller() !== null
  }

  async open(name: string, size: number): Promise<IFileSink> {
    if (this.picker !== null) {
      return this.openPicker(name)
    }
    const controller = this.channel.controller()
    if (controller === null) {
      throw new Error(EnumFileSaveError.NOT_SUPPORTED)
    }
    const id = uuidv4()
    const ports = this.channel.createPorts()
    const start: IDownloadStart = { type: EnumDownloadMessage.START, id, name, size }
    controller.postMessage(start, [ports.worker])
    this.channel.navigate(DOWNLOAD_PATH + id)

    return new WorkerSink(ports.page, size)
  }

  /** Окно «Сохранить как»: отказ пользователя — AbortError */
  private async openPicker(name: string): Promise<IFileSink> {
    let handle: IFileHandle
    try {
      handle = await (this.picker as SaveFilePicker)({ suggestedName: name })
    } catch {
      throw new Error(EnumFileSaveError.CANCELLED)
    }

    return new PickerSink(await handle.createWritable())
  }
}

export default function (): IFileSaver {
  return FileSaverV1.getInstance()
}
